import { useState, useRef, useEffect } from "react";

import {
  Bell,
  Check,
  CheckCheck,
  Clock,
  Heart,
  Loader2,
  MessageCircle,
  Sparkles,
  Trash2,
  X,
  XCircle,
  Zap,
} from "lucide-react";
import PropTypes from "prop-types";

import { Button } from "../ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

// ===== NOTIFICATION TYPE CONFIG =====
const NOTIF_ICONS = {
  TASK_COMPLETE: {
    icon: Check,
    color: "text-emerald-500",
    bg: "bg-emerald-50 dark:bg-emerald-900/20",
  },
  TASK_FAILED: {
    icon: X,
    color: "text-red-500",
    bg: "bg-red-50 dark:bg-red-900/20",
  },
  SOCIAL_LIKE: {
    icon: Heart,
    color: "text-pink-500",
    bg: "bg-pink-50 dark:bg-pink-900/20",
  },
  SOCIAL_COMMENT: {
    icon: MessageCircle,
    color: "text-blue-500",
    bg: "bg-blue-50 dark:bg-blue-900/20",
  },
  SYSTEM: {
    icon: Sparkles,
    color: "text-amber-500",
    bg: "bg-amber-50 dark:bg-amber-900/20",
  },
  CREDIT_LOW: {
    icon: Zap,
    color: "text-red-500",
    bg: "bg-red-50 dark:bg-red-900/20",
  },
  // Payment notifications
  PAYMENT_PENDING: {
    icon: Loader2,
    color: "text-blue-500",
    bg: "bg-blue-50 dark:bg-blue-900/20",
    animate: true,
  },
  PAYMENT_COMPLETE: {
    icon: Check,
    color: "text-emerald-500",
    bg: "bg-emerald-50 dark:bg-emerald-900/20",
  },
  PAYMENT_FAILED: {
    icon: X,
    color: "text-red-500",
    bg: "bg-red-50 dark:bg-red-900/20",
  },
};

// ===== TIME AGO HELPER =====
const timeAgo = (date) => {
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(date).toLocaleDateString();
};

// ===== SINGLE NOTIFICATION ITEM =====
const NotificationItem = ({ notification, onMarkRead, onDelete }) => {
  const config = NOTIF_ICONS[notification.type] || NOTIF_ICONS.SYSTEM;
  const Icon = config.icon;

  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-lg transition-colors cursor-pointer ${
        notification.is_read
          ? "opacity-60 hover:opacity-80"
          : "bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800"
      }`}
      onClick={() => !notification.is_read && onMarkRead(notification.id)}
      onKeyDown={(e) =>
        e.key === "Enter" &&
        !notification.is_read &&
        onMarkRead(notification.id)
      }
      role="button"
      tabIndex={0}
    >
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${config.bg}`}
      >
        <Icon
          size={14}
          className={`${config.color} ${config.animate ? "animate-spin" : ""}`}
        />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-foreground truncate">
          {notification.title}
        </p>
        <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">
          {notification.message}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <Clock size={10} className="text-muted-foreground" />
          <span className="text-[10px] text-muted-foreground">
            {timeAgo(notification.created_at)}
          </span>
          {!notification.is_read && (
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
          )}
        </div>
      </div>

      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete(notification.id);
        }}
        className="p-1 text-muted-foreground hover:text-red-500 rounded opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
        aria-label="Delete notification"
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
};

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    is_read: PropTypes.bool.isRequired,
    created_at: PropTypes.string.isRequired,
  }).isRequired,
  onMarkRead: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

// ===== NOTIFICATION BELL =====
const NotificationBell = ({
  notifications = [],
  unreadCount = 0,
  onMarkRead,
  onDelete,
  onClearAll,
  collapsed = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
    }
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen]);

  const bellButton = (
    <Button
      variant="ghost"
      size="icon"
      className="h-9 w-9 text-muted-foreground hover:text-foreground relative"
      onClick={() => setIsOpen(!isOpen)}
      aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
    >
      <Bell size={18} />
      {unreadCount > 0 && (
        <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-bold bg-red-500 text-white rounded-full">
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      )}
    </Button>
  );

  return (
    <div className="relative" ref={dropdownRef}>
      {collapsed ? (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{bellButton}</TooltipTrigger>
          <TooltipContent side="right">
            <p>Notifications{unreadCount > 0 ? ` (${unreadCount})` : ""}</p>
          </TooltipContent>
        </Tooltip>
      ) : (
        bellButton
      )}

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute bottom-full left-0 mb-2 w-80 max-h-96 bg-background border border-border rounded-xl shadow-xl z-50 flex flex-col overflow-hidden animate-in slide-in-from-bottom-2 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground">
              Notifications
            </h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={() => onMarkRead("all")}
                  className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                >
                  <CheckCheck size={12} />
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={() => {
                    if (confirm("Clear all notifications?")) {
                      onClearAll?.();
                    }
                  }}
                  className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-red-500 transition-colors"
                >
                  <XCircle size={12} />
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <Bell size={24} className="mb-2 opacity-40" />
                <p className="text-xs">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <NotificationItem
                  key={notif.id}
                  notification={notif}
                  onMarkRead={onMarkRead}
                  onDelete={onDelete}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

NotificationBell.propTypes = {
  notifications: PropTypes.array,
  unreadCount: PropTypes.number,
  onMarkRead: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClearAll: PropTypes.func,
  collapsed: PropTypes.bool,
};

export default NotificationBell;
